// Per-dialect synthesizer for named-DEFAULT-object add/drop ops (v2 Slice B5).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice B5.
//
// mssql exposes DEFAULT as a first-class named constraint object via two
// syntactic forms:
//   - inline (modern):  `ALTER TABLE … ADD CONSTRAINT df_name DEFAULT (expr)
//                        FOR col`. Drop via `ALTER TABLE … DROP CONSTRAINT
//                        df_name`. The constraint is bound to a specific
//                        column at creation time.
//   - standalone:       `CREATE DEFAULT df_name AS expr` (deprecated since
//                        SQL Server 2008 but still accepted). Drop via
//                        `DROP DEFAULT df_name`. The DEFAULT exists as a
//                        free-floating object that must be bound to a column
//                        via `sp_bindefault` separately. Opt in via
//                        `namedDefault.standalone: true` / `standalone: true`
//                        on the drop edit.
//
// pgsql / mysql / oracle / sqlite: defaults are column attributes, not first-
// class named objects — the synthesizer throws with a redirect to the
// `alter-column` op's `change.default` field (v2 Slice A1).

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { NamedDefaultDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

function tableText(engine: SqlEngine, table: QualifiedName): string {
  if (table.raw && table.raw.length > 0) return table.raw;
  const name = maybeQuoteIdent(engine, table.name, table.quoted);
  if (table.schema) {
    const schema = maybeQuoteIdent(engine, table.schema, table.quoted);
    return `${schema}.${name}`;
  }
  return name;
}

function rejectNonMssql(engine: SqlEngine, op: 'add' | 'drop'): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support named DEFAULT objects — defaults `
      + `on ${engine} are column attributes, not first-class named objects. `
      + `Use the 'alter-column' op with 'change.default' (v2 Slice A1) to `
      + `${op === 'add' ? 'set' : 'drop'} a column default on ${engine}.`,
  );
}

function validateDef(def: NamedDefaultDef): void {
  if (!def.name || def.name.length === 0) {
    throw new Error(`applyDdlEdit: named DEFAULT requires a non-empty 'name'`);
  }
  if (!def.expression || def.expression.trim().length === 0) {
    throw new Error(
      `applyDdlEdit: named DEFAULT requires a non-empty 'expression'`,
    );
  }
}

export function synthesizeAddNamedDefault(
  engine: SqlEngine,
  table: QualifiedName | undefined,
  def: NamedDefaultDef,
): string {
  if (engine !== 'mssql') rejectNonMssql(engine, 'add');
  validateDef(def);

  if (def.standalone) {
    // Deprecated since SQL Server 2008 but still accepted. The standalone
    // CREATE DEFAULT form does not bind to a column — consumers must call
    // sp_bindefault separately.
    return `CREATE DEFAULT ${def.name} AS ${def.expression}`;
  }

  // Inline ALTER TABLE form (modern, recommended).
  if (!table) {
    throw new Error(
      `applyDdlEdit: inline add-named-default requires a CREATE TABLE or `
        + `ALTER TABLE source statement so the synthesizer can bind the `
        + `default to a column on the source's table — got a different `
        + `source kind. Use 'standalone: true' for a free-floating CREATE `
        + `DEFAULT object.`,
    );
  }
  if (!def.column || def.column.length === 0) {
    throw new Error(
      `applyDdlEdit: inline add-named-default requires 'namedDefault.column' `
        + `to name the column the DEFAULT binds to (mssql syntax: `
        + `ADD CONSTRAINT name DEFAULT (expr) FOR col). Use `
        + `'standalone: true' for the free-floating CREATE DEFAULT form `
        + `(no column needed).`,
    );
  }
  const tbl = tableText(engine, table);
  return `ALTER TABLE ${tbl} ADD CONSTRAINT ${def.name} DEFAULT (${def.expression}) FOR ${def.column}`;
}

export function synthesizeDropNamedDefault(
  engine: SqlEngine,
  table: QualifiedName | undefined,
  constraintName: string,
  standalone: boolean | undefined,
): string {
  if (engine !== 'mssql') rejectNonMssql(engine, 'drop');
  if (!constraintName || constraintName.length === 0) {
    throw new Error(
      `applyDdlEdit: mssql drop-named-default requires 'constraintName'`,
    );
  }

  if (standalone) {
    // Drops a free-floating DEFAULT object created via CREATE DEFAULT.
    return `DROP DEFAULT ${constraintName}`;
  }

  // Drops the inline ADD CONSTRAINT … DEFAULT form.
  if (!table) {
    throw new Error(
      `applyDdlEdit: inline drop-named-default requires a CREATE TABLE or `
        + `ALTER TABLE source statement so the synthesizer can target the `
        + `right table for the DROP CONSTRAINT clause — got a different `
        + `source kind. Use 'standalone: true' for DROP DEFAULT (no table `
        + `needed).`,
    );
  }
  const tbl = tableText(engine, table);
  return `ALTER TABLE ${tbl} DROP CONSTRAINT ${constraintName}`;
}
